import { useEffect } from 'react';
import { CircleAlert, CircleCheck, X } from 'lucide-react';
import { useToastStore, type Toast } from '@/stores/toast.store';
import { cn } from '@/utils/cn';

const VISIBLE_MS = 5000;

function ToastItem({ toast }: { toast: Toast }) {
  const dismiss = useToastStore((s) => s.dismiss);
  useEffect(() => {
    const timer = setTimeout(() => dismiss(toast.id), VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [toast.id, dismiss]);

  const Icon = toast.tone === 'success' ? CircleCheck : CircleAlert;
  return (
    <li className="pointer-events-auto flex items-start gap-2.5 rounded-lg bg-white p-3 text-sm text-ink-strong shadow-card ring-1 ring-table-head">
      <Icon
        className={cn(
          'mt-px size-5 shrink-0',
          toast.tone === 'success' ? 'text-success' : 'text-danger',
        )}
        aria-hidden
      />
      <p className="flex-1">{toast.message}</p>
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        aria-label="Dismiss"
        className="-m-1 rounded-md p-1 text-muted hover:text-ink"
      >
        <X className="size-4" />
      </button>
    </li>
  );
}

/** Mounted once in the app shell. The live region exists before any toast, so it is announced. */
export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed right-4 bottom-4 z-[60] w-[min(calc(100%-2rem),22rem)]"
    >
      <ul className="flex flex-col gap-2">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} />
        ))}
      </ul>
    </div>
  );
}
